import React from 'react'
import { CountryCodes } from '@e3dc-react/shell/Hooks/useCountryOptions'
import { UnitTypes } from 'src/Libs/types'
import { BatterySettingsField, DppBatteryStatus, GeneralFormFields } from '../types'
import { Ai360 } from '@e3dc-react/icons'
import { NumberField, SwitchField } from 'src/Libs/inputs/formik'
import MonthSelector from 'src/Elements/Selectors/MonthSelector'
import { Trans } from 'react-i18next'
import { Typography } from '@mui/material'

type FormKeys = {
  title: {
    [key: CountryCodes | string]: string
  }
  description: {
    [key: CountryCodes | string]: string
  }
}

type TranslationKeysType = {
  energyPriceSurcharge: FormKeys
  networkFees: FormKeys
  taxes: FormKeys
}

const translationKeys: TranslationKeysType = {
  energyPriceSurcharge: {
    title: {
      NLD: 'systemSettings.dynamic-prices.energy-price-surcharge-nl.title',
      general: 'systemSettings.dynamic-prices.energy-price-surcharge.title',
    },
    description: {
      NLD: 'systemSettings.dynamic-prices.energy-price-surcharge-nl.description',
      general: 'systemSettings.dynamic-prices.energy-price-surcharge.description',
    },
  },
  networkFees: {
    title: {
      NLD: 'systemSettings.dynamic-prices.network-fees-nl.title',
      general: 'systemSettings.dynamic-prices.network-fees.title',
    },
    description: {
      NLD: 'systemSettings.dynamic-prices.network-fees-nl.description',
      general: 'systemSettings.dynamic-prices.network-fees.description',
    },
  },
  taxes: {
    title: {
      NLD: 'systemSettings.dynamic-prices.taxes-nl.title',
      general: 'systemSettings.dynamic-prices.taxes.title',
    },
    description: {
      NLD: 'systemSettings.dynamic-prices.taxes-nl.description',
      general: 'systemSettings.dynamic-prices.taxes.description',
    },
  },
}

/**
 * Function to return general form fields with installation location based translation keys
 */
export const getGeneralFormFields = (countryCode: CountryCodes = CountryCodes.GERMANY): GeneralFormFields[] => {
  const allowedCountryCodes = [CountryCodes.NETHERLANDS]
  const fields = [
    {
      titleKey: allowedCountryCodes.includes(countryCode)
        ? translationKeys.energyPriceSurcharge.title[countryCode]
        : translationKeys.energyPriceSurcharge.title.general,
      descriptionKey: allowedCountryCodes.includes(countryCode)
        ? translationKeys.energyPriceSurcharge.description[countryCode]
        : translationKeys.energyPriceSurcharge.description.general,
      fieldName: 'energyPriceSurcharge',
      fieldSuffix: UnitTypes.percentage,
      isDecimal: true,
      step: 0.01,
      precision: 2,
    },
    {
      titleKey: allowedCountryCodes.includes(countryCode)
        ? translationKeys.networkFees.title[countryCode]
        : translationKeys.networkFees.title.general,
      descriptionKey: allowedCountryCodes.includes(countryCode)
        ? translationKeys.networkFees.description[countryCode]
        : translationKeys.networkFees.description.general,
      fieldName: 'networkFees',
      fieldSuffix: UnitTypes.centsPerKiloWattHour,
      isDecimal: true,
      step: 0.001,
      precision: 3,
    },
    {
      titleKey: allowedCountryCodes.includes(countryCode) ? translationKeys.taxes.title[countryCode] : translationKeys.taxes.title.general,
      descriptionKey: allowedCountryCodes.includes(countryCode)
        ? translationKeys.taxes.description[countryCode]
        : translationKeys.taxes.description.general,
      fieldName: 'taxes',
      fieldSuffix: UnitTypes.centsPerKiloWattHour,
      isDecimal: true,
      step: 0.001,
      precision: 3,
    },
  ]
  return fields
}

/**
 * Function to return battery form fields based on the feature status and ems availability
 */
export const getBatterySettingsFields = (
  hasWritingRights: boolean,
  isEmsAvailable: boolean,
  isEmsV2Available: boolean,
  dppBatteryStatus: DppBatteryStatus,
  selectedMonthList: number[],
  handleUpdateSelectedMonthList: (updatedSelectedMonthList: number[]) => void,
  t: (translation: string) => string
): BatterySettingsField[] => {
  const allFields: BatterySettingsField[] = [
    {
      key: 'ems-based-price-limit-battery',
      titleIcon: <Ai360 />,
      descriptionKey: 'systemSettings-dynamic-prices.battery.ems-based-price-limit.description',
      suffix: (
        <SwitchField
          name={'isSgReadyEnabled'}
          sx={{ marginLeft: '0px' }}
          label={
            <Trans
              i18nKey={t('systemSettings.dynamic-prices.battery.360-ai-sg-ready')}
              components={{ bold: <Typography variant="h5" component={'span'} /> }}
            />
          }
          labelPlacement="start"
          disabled={!hasWritingRights}
        />
      ),
    },
    {
      key: 'price-limit-battery',
      titleKey: 'systemSettings-dynamic-prices.battery.price-limit.title',
      descriptionKey: 'systemSettings-dynamic-prices.battery.price-limit.description',
      suffix: (
        <NumberField
          name={'priceLimitBattery'}
          suffix={UnitTypes.centsPerKiloWattHour}
          isDecimal={true}
          step={0.001}
          precision={3}
          disabled={!hasWritingRights}
        />
      ),
    },
    {
      key: 'active-months',
      titleKey: 'systemSettings-dynamic-prices.battery.months-active.title',
      descriptionKey: 'systemSettings-dynamic-prices.battery.months-active.description',
      suffix: (
        <MonthSelector selectedMonthList={selectedMonthList} onChangeSelectedMonthList={handleUpdateSelectedMonthList} disabled={!hasWritingRights} />
      ),
    },
    {
      key: 'battery-state-of-charge-limit',
      titleKey: 'systemSettings-dynamic-prices.battery.state-of-charge-limit.title',
      descriptionKey: 'systemSettings-dynamic-prices.battery.state-of-charge-limit.description',
      suffix: (
        <NumberField
          name={'batteryStateOfChargeLimit'}
          suffix={UnitTypes.percentage}
          isDecimal={false}
          step={1}
          precision={0}
          disabled={!hasWritingRights}
        />
      ),
    },
  ]

  if (isEmsV2Available) {
    if (dppBatteryStatus === 'enabledWithDynamicEms') {
      return []
    }

    if (dppBatteryStatus === 'enabled') {
      return allFields.filter(field => field.key !== 'ems-based-price-limit-battery')
    }
  }

  if (!isEmsAvailable) {
    return allFields.filter(field => field.key !== 'ems-based-price-limit-battery')
  }

  if (isEmsAvailable && dppBatteryStatus === 'enabledWithDynamicEms') {
    return allFields.filter(
      field => field.key !== 'price-limit-battery' && field.key !== 'active-months' && field.key !== 'battery-state-of-charge-limit'
    )
  }

  if (isEmsAvailable && dppBatteryStatus === 'enabled') {
    return allFields.filter(field => field.key !== 'ems-based-price-limit-battery')
  }

  return []
}
